import { Label } from '@nrg/design-system';
import {
  type ChangeEvent,
  type ClipboardEvent,
  type KeyboardEvent,
  type ReactNode,
  useId,
  useRef,
} from 'react';
import styles from './CodeField.module.css';

const LENGTH = 6;

export interface CodeFieldProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: string | null | undefined;
  disabled?: boolean;
  autoFocus?: boolean;
}

export default function CodeField({
  label,
  value,
  onChange,
  onComplete,
  error,
  disabled,
  autoFocus,
}: CodeFieldProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const reactId = useId();
  const labelId = `code-${reactId}`;
  const errorId = error ? `${labelId}-error` : undefined;

  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? '');

  const focusBox = (index: number) => {
    const next = refs.current[index];
    next?.focus();
    next?.select();
  };

  const setDigits = (next: string) => {
    onChange(next);
    if (next.length === LENGTH && onComplete) {
      onComplete(next);
    }
  };

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '');
    if (!raw) {
      const next = digits.slice();
      next[index] = '';
      setDigits(next.join(''));
      return;
    }
    const next = digits.slice();
    let cursor = index;
    for (const char of raw) {
      if (cursor >= LENGTH) break;
      next[cursor] = char;
      cursor += 1;
    }
    setDigits(next.join('').slice(0, LENGTH));
    if (cursor < LENGTH) {
      focusBox(cursor);
    } else {
      refs.current[LENGTH - 1]?.blur();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      if (digits[index]) {
        const next = digits.slice();
        next[index] = '';
        setDigits(next.join(''));
        return;
      }
      if (index > 0) {
        event.preventDefault();
        const next = digits.slice();
        next[index - 1] = '';
        setDigits(next.join(''));
        focusBox(index - 1);
      }
      return;
    }
    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
    }
    if (event.key === 'ArrowRight' && index < LENGTH - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasted) return;
    event.preventDefault();
    const next = digits.slice();
    let cursor = index;
    for (const char of pasted) {
      if (cursor >= LENGTH) break;
      next[cursor] = char;
      cursor += 1;
    }
    setDigits(next.join('').slice(0, LENGTH));
    focusBox(Math.min(cursor, LENGTH - 1));
  };

  return (
    <fieldset className={styles.fieldset} {...(errorId ? { 'aria-describedby': errorId } : {})}>
      <legend
        id={labelId}
        style={{
          padding: 0,
          marginBottom: 'var(--nrg-space-1-5)',
        }}
      >
        <Label as="span" size="sm" tone="strong" spacing="wider">
          {label}
        </Label>
      </legend>
      <div className={styles.boxes}>
        {digits.map((digit, index) => (
          <input
            // biome-ignore lint/suspicious/noArrayIndexKey: positional index identifies the OTP slot
            key={index}
            ref={(node) => {
              refs.current[index] = node;
            }}
            className={styles.box}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            value={digit}
            onChange={(event) => handleChange(index, event)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            disabled={disabled}
            data-filled={digit ? 'true' : 'false'}
            data-invalid={error ? 'true' : 'false'}
            aria-label={`Digit ${index + 1}`}
            aria-invalid={error ? true : undefined}
            // biome-ignore lint/a11y/noAutofocus: explicit consumer opt-in; auth flow needs initial focus on the OTP entry
            autoFocus={autoFocus && index === 0}
          />
        ))}
      </div>
      {error ? (
        <span id={errorId} role="alert" className={styles.error}>
          {error}
        </span>
      ) : null}
    </fieldset>
  );
}
